import { useDataCapture } from '@/domains/jobs/hooks/useDataCapture';
import {
  type ParsedFormGroup,
  countAnswers,
  parseFormResponses,
} from '@/domains/jobs/lib/parseFormResponses';
import { EmptyState } from '@/shared/components/EmptyState';
import { ImageWithFallback } from '@/shared/components/ImageWithFallback';
import { formatDateTime } from '@/shared/lib/date-utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import { Camera, FileText } from 'lucide-react';
import { useMemo } from 'react';

interface FormResponse {
  question: string;
  answer: string;
}

interface DataCaptureTabProps {
  jobId: string;
  formResponses?: FormResponse[];
  capturedImages?: string[];
}

export function DataCaptureTab({
  jobId,
  formResponses: propFormResponses,
  capturedImages: propCapturedImages,
}: DataCaptureTabProps) {
  const numericJobId = Number(jobId);
  const { data: apiData, isLoading } = useDataCapture(numericJobId);

  // Group the API's form_responses into one entry per submitted form so each
  // form renders in its own panel with its own section headings and photos
  // (WRK-5765). The `formResponses` prop is a legacy flat override used by
  // callers that pass pre-parsed question/answer pairs; when supplied it is
  // shown as a single unnamed group.
  const formGroups = useMemo<ParsedFormGroup[]>(() => {
    if (propFormResponses && propFormResponses.length > 0) {
      return [
        {
          key: 'provided',
          formName: 'Form Responses',
          submittedAt: null,
          sections: [
            {
              sectionName: '',
              answers: propFormResponses.map((r) => ({ ...r, images: [] })),
            },
          ],
        },
      ];
    }
    return parseFormResponses(apiData?.form_responses ?? []);
  }, [propFormResponses, apiData]);

  // Only render panels that actually captured something. Jobs commonly carry
  // placeholder rows for forms that were assigned but never filled in.
  const populatedGroups = useMemo(
    () => formGroups.filter((group) => countAnswers(group) > 0),
    [formGroups],
  );

  // Photos captured inside form_data are display-only — they live in the JSON,
  // not the images table, so they can't be individually deleted. A caller may
  // override them wholesale via the legacy `capturedImages` prop.
  const overrideImages = useMemo<string[]>(() => propCapturedImages ?? [], [propCapturedImages]);

  // First form expanded by default: with 3-4 forms of 15+ questions each,
  // expanding everything makes the tab unreadably long.
  const defaultOpenGroup = populatedGroups[0]?.key;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
        <span className="ml-3 text-muted-foreground text-sm">Loading data capture...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* The Additional Images and Quality Check Notes sections were removed in
          WRK-5808 — they were in the Figma but not the BRD. Form Responses is
          now the tab's only content, so it needs its own empty state. */}
      {populatedGroups.length === 0 ? (
        <EmptyState
          icon={Camera}
          title="No data captured yet"
          description="Form responses submitted from the mobile app will appear here."
        />
      ) : (
        /* Form Responses — one collapsible panel per submitted form */
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="size-5 text-primary" />
            <h3 className="font-semibold text-lg">Form Responses</h3>
            <span className="text-sm text-muted-foreground">
              ({populatedGroups.length} form{populatedGroups.length === 1 ? '' : 's'})
            </span>
          </div>

          <Accordion type="multiple" defaultValue={defaultOpenGroup ? [defaultOpenGroup] : []}>
            {populatedGroups.map((group) => (
              <AccordionItem
                key={group.key}
                value={group.key}
                data-testid={`form-response-group-${group.key}`}
              >
                <AccordionTrigger className="text-left">
                  <span className="flex flex-col gap-0.5">
                    <span className="font-semibold">{group.formName}</span>
                    {group.submittedAt && (
                      <span className="text-xs font-normal text-muted-foreground">
                        Submitted {formatDateTime(group.submittedAt)}
                      </span>
                    )}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-6">
                    {group.sections.map((section, sectionIndex) => (
                      <div key={`${group.key}-${sectionIndex}`}>
                        {/* Only label sections when a form has more than one —
                            a single section repeats the form name otherwise. */}
                        {group.sections.length > 1 && section.sectionName && (
                          <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground mb-3">
                            {section.sectionName}
                          </h4>
                        )}
                        <div className="space-y-4">
                          {section.answers.map((answer, answerIndex) => {
                            const images =
                              overrideImages.length > 0 && answer.images.length > 0
                                ? overrideImages
                                : answer.images;

                            return (
                              <div
                                key={`${group.key}-${sectionIndex}-${answerIndex}`}
                                // A conditional branch answer is indented under the
                                // question that revealed it, so it reads as part of
                                // that question rather than a question of its own.
                                className={
                                  answer.nested ? 'ml-4 pl-4 border-l-2 border-border' : undefined
                                }
                              >
                                <div className="border-b border-border pb-4 last:border-0 last:pb-0">
                                  <p className="font-medium text-foreground mb-2">
                                    {answer.question}
                                  </p>
                                  {answer.answer && (
                                    <p className="text-muted-foreground">{answer.answer}</p>
                                  )}
                                </div>

                                {/* Photos captured against this specific question */}
                                {images.length > 0 && (
                                  <div className="mt-4 mb-4">
                                    <div className="grid grid-cols-3 gap-3">
                                      {images.map((imageUrl, imgIndex) => (
                                        <div
                                          key={imageUrl}
                                          className="relative aspect-video rounded-lg overflow-hidden border border-border bg-muted"
                                        >
                                          <ImageWithFallback
                                            src={imageUrl}
                                            alt={`${answer.question} photo ${imgIndex + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
